import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReportType, GeneratedReport } from '@m8-app/contracts/rest/admin.js';
import { downloadReport, getReportHistory, runReport } from '../api/admin.js';

// ── Types ─────────────────────────────────────────────────────────────────────

type ReportFormat = 'pdf' | 'excel' | 'csv';

// ── Constants ─────────────────────────────────────────────────────────────────

const REPORT_CATEGORIES = [
  {
    category: 'Devices',
    reports: [
      { value: 'devices_by_type',        label: 'Device List',                  description: 'All devices with type, owner, state and lifecycle' },
      { value: 'devices_online_offline',  label: 'Online / Offline Status',      description: 'Current presence status of all devices' },
      { value: 'devices_registered',      label: 'Registered Devices',           description: 'Devices registered in a given period' },
      { value: 'devices_deactivated',     label: 'Deactivated Devices',          description: 'Devices deactivated with reason and date' },
      { value: 'devices_lifecycle',       label: 'Device Lifecycle History',     description: 'Full lifecycle events per device' },
      { value: 'devices_by_product',      label: 'Devices by Product',           description: 'Count and list grouped by M8 / COP-LOP / Avira' },
    ],
  },
  {
    category: 'Users',
    reports: [
      { value: 'user_activity',           label: 'User List',                    description: 'All users with role, device and status' },
      { value: 'user_blocked',            label: 'Blocked Users',                description: 'Users currently blocked with block date' },
      { value: 'user_by_role',            label: 'Users by Role',                description: 'Count and list grouped by role' },
      { value: 'user_new_registrations',  label: 'New Registrations',            description: 'Users who registered in a given period' },
      { value: 'staff_list',              label: 'Staff / Admin List',           description: 'All Haron internal staff accounts and roles' },
      { value: 'installer_list',          label: 'Installer List',               description: 'All registered installers and assigned devices' },
    ],
  },
  {
    category: 'Firmware',
    reports: [
      { value: 'firmware_rollout',        label: 'Firmware Rollout History',     description: 'Rollout history, success/failure per device' },
      { value: 'firmware_versions',       label: 'Firmware Versions',            description: 'All uploaded firmware versions and publish status' },
      { value: 'firmware_pending',        label: 'Devices Pending Update',       description: 'Devices not yet on the latest firmware' },
    ],
  },
  {
    category: 'Service & Warranty',
    reports: [
      { value: 'service_requests',        label: 'Service Requests',             description: 'All service requests with status and assignee' },
      { value: 'service_open',            label: 'Open Service Requests',        description: 'Unresolved service requests' },
      { value: 'warranty_claims',         label: 'Warranty Claims',              description: 'All warranty claims with status and device' },
      { value: 'warranty_expiring',       label: 'Expiring Warranties',          description: 'Devices whose warranty expires within 30 days' },
    ],
  },
  {
    category: 'System',
    reports: [
      { value: 'commands_summary',        label: 'Commands Summary',             description: 'Remote actions sent — by type and result' },
      { value: 'system_health',           label: 'System Health',                description: 'Uptime, MQTT broker stats, DB metrics' },
      { value: 'audit_log',               label: 'Audit Log',                    description: 'All admin actions with actor and timestamp' },
    ],
  },
];

const REPORT_TYPES = REPORT_CATEGORIES.flatMap((c) => c.reports) as { value: ReportType; label: string; description: string }[];

const FORMATS: { value: ReportFormat; label: string; icon: string }[] = [
  { value: 'pdf',   label: 'PDF',   icon: '📄' },
  { value: 'excel', label: 'Excel', icon: '📊' },
  { value: 'csv',   label: 'CSV',   icon: '📋' },
];

const FORMAT_COLORS: Record<ReportFormat, string> = {
  pdf:   'bg-red-50 text-red-700 border border-red-200',
  excel: 'bg-green-50 text-green-700 border border-green-200',
  csv:   'bg-blue-50 text-blue-700 border border-blue-200',
};

const STATUS_COLORS: Record<GeneratedReport['status'], string> = {
  ready:      'bg-green-50 text-green-700',
  generating: 'bg-amber-50 text-amber-700',
  failed:     'bg-red-50 text-red-700',
};

function toDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Reports() {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 86_400_000);
  const qc = useQueryClient();

  const [type, setType] = useState<ReportType>('devices_by_type');
  const [format, setFormat] = useState<ReportFormat>('excel');
  const [from, setFrom] = useState(toDateString(weekAgo));
  const [to, setTo] = useState(toDateString(now));
  const [generating, setGenerating] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const { data: historyData } = useQuery({
    queryKey: ['reportHistory'],
    queryFn: () => getReportHistory(),
  });
  const history = historyData?.reports ?? [];

  const selectedType = REPORT_TYPES.find((r) => r.value === type)!;

  async function run() {
    setGenerating(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      await runReport({
        type,
        range: { from: `${from}T00:00:00Z`, to: `${to}T23:59:59Z` },
        format: format === 'pdf' ? 'pdf' : format === 'excel' ? 'excel' : 'csv',
      });
      await qc.invalidateQueries({ queryKey: ['reportHistory'] });
      setSuccessMsg(`${selectedType.label} report generated successfully.`);
    } catch {
      setErrorMsg('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await run();
  }

  async function handleDownload(id: string, filename: string) {
    try {
      const blob = await downloadReport(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  }

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Reports</h2>
          <p className="text-sm text-gray-500">Generate and download reports. All generated reports are saved in the history below.</p>
        </div>
      </div>

      {/* Generate form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Generate New Report</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Report type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="reportType" className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
              <select
                id="reportType"
                value={type}
                onChange={(e) => setType(e.target.value as ReportType)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {REPORT_CATEGORIES.map((cat) => (
                  <optgroup key={cat.category} label={cat.category}>
                    {cat.reports.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <p className="text-xs text-gray-400 pb-2">{selectedType?.description}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {/* Date range */}
            <div>
              <label htmlFor="rangeFrom" className="block text-sm font-medium text-gray-700 mb-1">From</label>
              <input
                id="rangeFrom"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label htmlFor="rangeTo" className="block text-sm font-medium text-gray-700 mb-1">To</label>
              <input
                id="rangeTo"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            {/* Format */}
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">Format</span>
              <div className="flex gap-2">
                {FORMATS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFormat(f.value)}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      format === f.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span>{f.icon}</span> {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {successMsg && (
            <p className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">{successMsg}</p>
          )}
          {errorMsg && (
            <div className="flex items-center justify-between gap-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <span>{errorMsg}</span>
              <button
                type="button"
                onClick={() => void run()}
                disabled={generating}
                className="shrink-0 px-3 py-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md text-xs font-medium"
              >
                {generating ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={generating}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {generating ? 'Generating…' : 'Generate Report'}
          </button>
        </form>
      </div>

      {/* History table */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Generated Reports</h3>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Report Type</th>
                <th className="px-4 py-3 font-medium">Format</th>
                <th className="px-4 py-3 font-medium">Duration</th>
                <th className="px-4 py-3 font-medium">Generated By</th>
                <th className="px-4 py-3 font-medium">Generated At</th>
                <th className="px-4 py-3 font-medium">Rows</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">No reports generated yet</td></tr>
              )}
              {history.map((r) => {
                const typeLabel = REPORT_TYPES.find((t) => t.value === r.type)?.label ?? r.type;
                const formatKey = r.format === 'json' ? 'csv' : r.format as ReportFormat;
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{typeLabel}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${FORMAT_COLORS[formatKey]}`}>
                        {r.format.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {r.rangeFrom.slice(0, 10)} → {r.rangeTo.slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.generatedByMobile}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{r.generatedAt.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-3 text-gray-500">{r.status === 'ready' ? r.rowCount : '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[r.status]}`}>
                        {r.status === 'generating' ? '● Generating…' : r.status === 'failed' ? '✕ Failed' : '✓ Ready'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'ready' ? (
                        <button
                          onClick={() => handleDownload(r.id, `${r.type}_${r.id.slice(0, 8)}.${r.format}`)}
                          className="text-xs text-indigo-600 hover:underline font-medium"
                        >
                          Download
                        </button>
                      ) : r.status === 'failed' ? (
                        <span className="text-xs text-red-400">Failed</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
